import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import config from "../config/config";

const AboutUsPage = () => {
  const location = useLocation();
  const [aboutContent, setAboutContent] = useState({
    subtitle: "Manufacturing Excellence Since 1990",
    description:
      "NCG Corporate is a leading manufacturer and exporter of customized engineering steel products with over 30 years of experience in quality manufacturing, sourcing, procurement, and supply chain management. Our company is led by a Quality certified auditor and Six Sigma certified director, ensuring the highest standards in all our operations. We are committed to providing complete solutions and seamless supply chain services to our clients worldwide.",
    vision:
      "To be the global leader in customized engineering solutions, recognized for innovation, quality, and customer satisfaction. We envision a world where our products contribute to building sustainable infrastructure and improving lives.",
    mission:
      "To deliver exceptional customized engineering steel products through continuous innovation, superior quality, and sustainable practices. We are committed to building long-term partnerships with our clients by exceeding their expectations and providing end-to-end solutions.",
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [infrastructure, setInfrastructure] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback data functions
  const getFallbackTeamMembers = () => {
    return [
      {
        id: 1,
        name: "John Smith",
        position: "Chief Executive Officer",
        bio: "John has over 20 years of experience in manufacturing and business leadership.",
        image_url: config.getImageUrl("img_1.png"),
        linkedin_url: "https://linkedin.com/in/johnsmith",
      },
      {
        id: 2,
        name: "Sarah Johnson",
        position: "Chief Technology Officer",
        bio: "Sarah leads our technical innovation and quality assurance initiatives.",
        image_url: config.getImageUrl("img_2.png"),
        linkedin_url: "https://linkedin.com/in/sarahjohnson",
      },
      {
        id: 3,
        name: "Michael Brown",
        position: "Head of Operations",
        bio: "Michael oversees our manufacturing operations and supply chain management.",
        image_url: config.getImageUrl("img_3.png"),
        linkedin_url: "https://linkedin.com/in/michaelbrown",
      },
    ];
  };

  const getFallbackInfrastructure = () => {
    return [
      {
        id: 1,
        title: "State-of-the-Art Manufacturing Facility",
        description:
          "Modern manufacturing facility equipped with advanced machinery and quality control systems.",
        image_url: config.getImageUrl("ncg_about.png"),
      },
      {
        id: 2,
        title: "Quality Control Laboratory",
        description:
          "Comprehensive testing and quality assurance laboratory ensuring product excellence.",
        image_url: config.getImageUrl("img_1.png"),
      },
      {
        id: 3,
        title: "Research & Development Center",
        description:
          "Innovation hub for developing next-generation products and solutions.",
        image_url: config.getImageUrl("img_2.png"),
      },
    ];
  };

  const getFallbackCertifications = () => {
    return [
      {
        id: 1,
        title: "ISO 9001:2015",
        cert_number: "ISO-9001-2024",
        issuing_body: "International Organization for Standardization",
        issued_date: "2024-01-01",
        expiry_date: "2027-01-01",
        image_url: config.getImageUrl("img_1.png"),
      },
      {
        id: 2,
        title: "Six Sigma Certification",
        cert_number: "SS-2024-001",
        issuing_body: "Six Sigma Institute",
        issued_date: "2024-01-01",
        expiry_date: "2026-01-01",
        image_url: config.getImageUrl("img_2.png"),
      },
      {
        id: 3,
        title: "Quality Management Certification",
        cert_number: "QMC-2024-001",
        issuing_body: "Quality Management Institute",
        issued_date: "2024-01-01",
        expiry_date: "2025-12-31",
        image_url: config.getImageUrl("img_3.png"),
      },
    ];
  };

  useEffect(() => {
    console.log("About Us page loading - fetching data from API");

    const fetchData = async () => {
      try {
        // Fetch About Us content
        const aboutResponse = await fetch(
          config.getAdminApiUrl("get_about_content.php"),
          {
            headers: config.getApiHeaders(),
          }
        );
        const aboutData = await aboutResponse.json();

        if (aboutData.success && aboutData.content && aboutData.content.main) {
          const content = aboutData.content.main;
          console.log("About Us content fetched:", content);
          setAboutContent({
            subtitle: content.subtitle || "Manufacturing Excellence Since 1990",
            description:
              content.description ||
              "NCG Corporate is a leading manufacturer and exporter of customized engineering steel products with over 30 years of experience in quality manufacturing, sourcing, procurement, and supply chain management. Our company is led by a Quality certified auditor and Six Sigma certified director, ensuring the highest standards in all our operations. We are committed to providing complete solutions and seamless supply chain services to our clients worldwide.",
            vision:
              content.vision ||
              "To be the global leader in customized engineering solutions, recognized for innovation, quality, and customer satisfaction. We envision a world where our products contribute to building sustainable infrastructure and improving lives.",
            mission:
              content.mission ||
              "To deliver exceptional customized engineering steel products through continuous innovation, superior quality, and sustainable practices. We are committed to building long-term partnerships with our clients by exceeding their expectations and providing end-to-end solutions.",
          });
          console.log("Description content:", content.description);
        }

        // Fetch team members
        const teamResponse = await fetch(
          config.getAdminApiUrl("get_team_members.php"),
          {
            headers: config.getApiHeaders(),
          }
        );
        const teamData = await teamResponse.json();

        if (teamData.success) {
          console.log("Team members fetched:", teamData.team_members);
          setTeamMembers(teamData.team_members || []);
        }

        // Fetch infrastructure
        const infrastructureResponse = await fetch(
          config.getAdminApiUrl("get_infrastructure.php"),
          {
            headers: config.getApiHeaders(),
          }
        );
        const infrastructureData = await infrastructureResponse.json();

        if (infrastructureData.success) {
          console.log(
            "Infrastructure fetched:",
            infrastructureData.infrastructure
          );
          setInfrastructure(infrastructureData.infrastructure || []);
        }

        // Fetch certifications
        const certificationsResponse = await fetch(
          config.getAdminApiUrl("get_certifications.php"),
          {
            headers: config.getApiHeaders(),
          }
        );
        const certificationsData = await certificationsResponse.json();

        if (certificationsData.success) {
          console.log(
            "Certifications fetched:",
            certificationsData.certifications
          );
          setCertifications(certificationsData.certifications || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load content. Using default content.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Handle smooth scrolling to hash on page load
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100); // Small delay to ensure page is rendered
      }
    }
  }, [location.hash]);
  if (loading) {
    return (
      <div className="container mt-5">
        <div className="row">
          <div className="col-12 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading About Us content...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="row">
          <div className="col-12 text-center">
            <div className="alert alert-warning" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* About Us Hero Section */}
      <section
        className="about-hero-section"
        style={{
          backgroundImage: `url(${config.getImageUrl("ncg_about.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="about-hero-title">About NCG Corporate</h1>
              <p className="about-hero-subtitle">{aboutContent.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="company-overview-section" id="company-overview">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="section-title">Company Overview</h2>
              <p className="section-text">{aboutContent.description}</p>
              <div className="company-stats">
                <div className="stat-item">
                  <h3>30+</h3>
                  <p>Years of Experience</p>
                </div>
                <div className="stat-item">
                  <h3>500+</h3>
                  <p>Satisfied Clients</p>
                </div>
                <div className="stat-item">
                  <h3>50+</h3>
                  <p>Countries Served</p>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="company-image-placeholder">
                <img
                  src={config.getImageUrl("abou_img.png")}
                  alt="About NCG Corporate - Manufacturing Excellence"
                  className="about-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision and Mission Section */}
      <section className="vision-mission-section" id="vision-and-mission">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="vision-card">
                <div className="vision-icon">
                  <i className="fas fa-eye"></i>
                </div>
                <h3>Our Vision</h3>
                <p>{aboutContent.vision}</p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="mission-card">
                <div className="mission-icon">
                  <i className="fas fa-bullseye"></i>
                </div>
                <h3>Our Mission</h3>
                <p>{aboutContent.mission}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="team-section" id="our-team">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h2 className="section-title">Our Leadership Team</h2>
              <p className="section-subtitle">
                Meet the experienced professionals driving our success
              </p>
            </div>
          </div>
          <div className="row">
            {(() => {
              console.log("Team members data:", teamMembers);
              return teamMembers.length > 0 ? (
                teamMembers.map((member) => (
                  <div key={member.id} className="col-lg-4 col-md-6">
                    <div className="team-member">
                      {member.image_url ? (
                        <img
                          src={member.image_url}
                          alt={member.name}
                          className="team-image"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "block";
                          }}
                        />
                      ) : null}
                      <div
                        className="team-image-placeholder"
                        style={{ display: member.image_url ? "none" : "block" }}
                      ></div>
                      <h4>{member.name}</h4>
                      <p className="team-position">{member.position}</p>
                      <p className="team-description">{member.description}</p>
                      {member.email && (
                        <p className="team-email">
                          <a href={`mailto:${member.email}`}>
                            <i className="fas fa-envelope me-2"></i>
                            {member.email}
                          </a>
                        </p>
                      )}
                      {member.linkedin_url && (
                        <p className="team-linkedin">
                          <a
                            href={member.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fab fa-linkedin me-2"></i>
                            LinkedIn Profile
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center">
                  <p>No team members found.</p>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* Infrastructure Section */}
      <section className="infrastructure-section" id="infrastructure">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h2 className="section-title">Our Infrastructure</h2>
              <p className="section-subtitle">
                State-of-the-art facilities and equipment
              </p>
            </div>
          </div>
          <div className="row">
            {infrastructure.length > 0 ? (
              infrastructure.map((item) => (
                <div key={item.id} className="col-lg-6 col-md-6">
                  <div className="infrastructure-item">
                    <div className="infrastructure-icon">
                      {item.icon ? (
                        <i className={item.icon}></i>
                      ) : (
                        <i className="fas fa-industry"></i>
                      )}
                    </div>
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center">
                <p>No infrastructure items found.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="certifications-section">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h2 className="section-title">Certifications & Standards</h2>
            </div>
          </div>
          <div className="row">
            {certifications.length > 0 ? (
              certifications.map((cert) => (
                <div key={cert.id} className="col-lg-3 col-md-6">
                  <div className="certification-card">
                    <div className="cert-icon">
                      {cert.icon ? (
                        <i className={cert.icon}></i>
                      ) : (
                        <i className="fas fa-certificate"></i>
                      )}
                    </div>
                    <h5>{cert.title}</h5>
                    <p>{cert.description}</p>
                    {cert.cert_number && (
                      <small className="cert-number">
                        Cert #: {cert.cert_number}
                      </small>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center">
                <p>No certifications found.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutUsPage;
